import React, { useEffect, useMemo } from "react";

import _, { set } from 'lodash';
import cx from 'classnames';

import { Outlet, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CollectionCard, DraggableCollectionCard, PlaceholderCollectionCard } from "./components/card";

import { localizedStrings } from '../../localization/index.jsx'

import { useDispatch, useSelector } from "react-redux";
import { CollectionLists } from "./components/list";
import { addCollectionToList, fetchCollections, invalidateCollectionCreationState, invalidateCollectionDeletionState, invalidateCollectionMoveState, invalidateCollections, invalidateCollectionUpdateState, removeCollectionFromList, updateCollection, updateCollectionList } from "../../redux/actions/collections";
import InfiniteScroll from "../../components/infiniteScroll";
import { SortByAction } from "./components/sortBy";
import { getSortPreference, persistSortPreference } from "../../services/collections.helper";
import { invalidateCollectionsSearchResults, searchCollections } from "../../redux/actions/search";
import { CloneHelpToolbar, CloneMenubar, CollectionsMenuBar, DeleteMenubar, DeleteSelectionToolbar, RenameMenubar } from "./components/edit";

import styles from "./collections.module.scss";
import { Collection, CollectionList } from "../../types/collections";
import { DndContext, DragOverlay, KeyboardSensor, MouseSensor, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { toast } from "react-toastify";
import Toast from "../../components/toast";
import EmptyMessage from "../../components/emptyMessage";
import { DEFAULT_COLLECTION_LIST } from "../../constants/collections";
import { fixCursorSnapOffset } from "src/services/dnd";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";
import { CreateCollectionDialog } from "./components/create";
import { useContentMapAdoption } from "src/redux/actions/adoption/contentMap";

export const useCollections = (folderId: string | undefined, query: string | null) => {
  return useSelector((state) => {
    let collectionState = _.get(state, 'collections');
    if (query) {
      collectionState = _.get(state, 'search.collections');
    } else if (folderId) {
      collectionState = _.get(state, `collections.filtered[${folderId}]`);
    }

    const collections = _.get(collectionState, 'items') || [];
    const loading = _.get(collectionState, 'loading');
    const pagination = _.get(collectionState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      collections,
      loading,
      hasMoreItems,
      nextPage,
      total
    };
  }, _.isEqual);
}

interface EditChange {
  id: string;
  type: 'collection' | 'collection_list';
  value: string | boolean | Collection | CollectionList | null | undefined;
}

interface CollectionsContextProps {
  sort: { sortBy: string, orderBy: string };
  editMode: 'create' | 'rename' | 'delete' | 'clone' | null | undefined;
  edits: EditChange[];
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  onEditChanges: (value: EditChange) => void;

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  onEditModeChange: (value: any) => void;
}

export const CollectionsContext = React.createContext<CollectionsContextProps>({
  sort: { sortBy: 'name', orderBy: 'asc' },
  editMode: null,
  edits: [] as EditChange[],

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  onEditModeChange: (value: any) => { },

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  onEditChanges: (value: EditChange) => { }
});

const CollectionsPage: React.FC<object> = () => {

  const dispatch = useDispatch();
  const [collectionContext, setCollectionContext] = React.useState<CollectionsContextProps>({
    sort: getSortPreference(),
    editMode: null,
    edits: [],
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    onEditChanges: (value: EditChange) => { },

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    onEditModeChange: (value: any) => { }
  });

  const { folderId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('query');

  const { trackContentSearched } = useContentMapAdoption();

  const [dragCollection, setDragCollection] = React.useState<Collection | null>(null);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  });

  const mouseSensor = useSensor(MouseSensor)
  const touchSensor = useSensor(TouchSensor)
  const keyboardSensor = useSensor(KeyboardSensor)

  const sensors = useSensors(
    mouseSensor,
    touchSensor,
    keyboardSensor,
    pointerSensor
  )

  useEffect(() => {
    let contextState = {
      sort: { sortBy: 'name', orderBy: 'asc' },
      editMode: null,
      edits: [],
      onEditChanges: handleEditChanges,
      onEditModeChange: handleEditModeChange
    };

    const sortPreference = getSortPreference();

    if (sortPreference) {
      contextState = {
        ...contextState,
        sort: sortPreference
      }
    }

    setCollectionContext((state) => {
      return {
        ...state,
        ...contextState
      }
    });
  }, []);

  const handleSortChange = (value: any) => {
    setCollectionContext((state) => {
      return {
        ...state,
        sort: value
      }
    });

    persistSortPreference(value.sortBy, value.orderBy);
  }

  const handleEditModeChange = (value: any) => {
    setCollectionContext((state) => {
      const edits = value != null ? state.edits : [];

      if (!value) {
        dispatch(invalidateCollectionUpdateState());
        dispatch(invalidateCollectionDeletionState());
        dispatch(invalidateCollectionCreationState());
      }

      return {
        ...state,
        editMode: value,
        edits: edits
      }
    });
  }

  const handleSearchChange = (value: string | null) => {
    if (value) {
      setSearchParams({ query: value });
      trackContentSearched({
        sourceCta: 'Collections',
        searchString: value,
        filter: 'Collections',
        searchTags: [],
        categories: []
      });
    } else {
      searchParams.delete('query');
      setSearchParams(searchParams);
    }
  }

  const handleEditChanges = (value: EditChange) => {
    setCollectionContext((state) => {
      const edits = [...state.edits];
      const index = _.findIndex(edits, { id: value.id });
      if (index >= 0) {
        edits[index] = value;
      } else {
        edits.push(value);
      }

      return {
        ...state,
        edits: edits
      }
    });
  }

  const handleRename = () => {
    const editedCollections = _.filter(collectionContext.edits, (edit) => {
      return edit.type === 'collection';
    }).filter(edit => {
      if (edit.value != null && _.size((edit.value as string).trim()) > 0) {
        return true;
      }

      return false;
    });

    const editedCollectionLists = _.filter(collectionContext.edits, (edit) => {
      return edit.type === 'collection_list';
    }).filter(edit => {
      if (edit.value != null && _.size((edit.value as string).trim()) > 0) {
        return true;
      }

      return false;
    });

    editedCollections.forEach((edit) => {
      dispatch(updateCollection({
        id: edit.id,
        request: { name: (edit.value as string).trim() }
      }));
    });

    editedCollectionLists.forEach((edit) => {
      dispatch(updateCollectionList({
        id: edit.id,
        request: { name: (edit.value as string).trim() }
      }));
    });

    if (_.size(editedCollections) == 0 && _.size(editedCollectionLists) == 0) {
      handleEditModeChange(null);
    }
  }

  const handleDragStart = (event: any) => {
    const collection = _.get(event, 'active.data.current.collection');
    setDragCollection(collection);
  }

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    const collection = _.get(active, 'data.current.collection');
    const targetList = _.get(over, 'data.current.list');

    if (collection && targetList) {
      if (!targetList.smart) {
        dispatch(addCollectionToList({
          source: folderId,
          target: targetList,
          collection: collection
        }));
      } else if (targetList.smart && targetList.id === 'all' && folderId) {
        const source = DEFAULT_COLLECTION_LIST.find((list) => list.id === folderId);
        if (source?.smart) {
          console.debug('Cannot move collection to smart list');
          return;
        }

        dispatch(removeCollectionFromList({
          source: folderId,
          target: targetList,
          collection: collection
        }));
      }
    }

    setDragCollection(null);
  }

  return (
    <CollectionsContext.Provider value={collectionContext}>
      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}
        autoScroll={false} collisionDetection={fixCursorSnapOffset}
        sensors={sensors}
      >
        <div className={styles.content}>
          <div className={styles.header}>
            <div className={styles.pageHeader}>
              <h1>{localizedStrings.pages.collections.title}</h1>
              <p>personalised content microsites</p>
            </div>
            <div className={styles.actionMenu}>
              <CollectionsMenuBar show={!_.includes(['rename', 'delete', 'clone'], collectionContext.editMode)}
                list={folderId}
                query={query}
                handleSearchChange={handleSearchChange}
                handleEditModeChange={handleEditModeChange}
              />
              <RenameMenubar show={collectionContext.editMode === 'rename'}
                list={folderId}
                onConfirm={handleRename}
                onCancel={() => handleEditModeChange(null)}
              />
              <DeleteMenubar show={collectionContext.editMode === 'delete'}
                onCancel={() => handleEditModeChange(null)}
              />
              <CloneMenubar show={collectionContext.editMode === 'clone'}
                onCancel={() => handleEditModeChange(null)}
              />
            </div>
          </div>
          <div className={styles.secondaryMenu}>
            <CollectionLists editMode={collectionContext.editMode} />
            <div className={styles.sortMenu}>
              <SortByAction value={collectionContext.sort} onChange={handleSortChange} />
            </div>
          </div>
          <Outlet />
          <DeleteSelectionToolbar
            show={collectionContext.editMode === 'delete'}
            collections={collectionContext.edits.filter((edit) => edit.value).map((edit) => edit.value as Collection)}
            onFinish={() => {
              handleEditModeChange(null);
            }}
          />
          <CloneHelpToolbar show={collectionContext.editMode === 'clone'} />
        </div>

        <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={null}>
          {dragCollection &&
            <DraggableCollectionCard item={dragCollection} />
          }
        </DragOverlay>

        <CollectionMoveStatus />

        <CreateCollectionDialog
          list={folderId}
          show={collectionContext.editMode === 'create'}
          onClose={() => {
            handleEditModeChange(null);
          }}
        />
      </DndContext>
    </CollectionsContext.Provider>
  );
};

const CollectionsGrid: React.FC<object> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { folderId } = useParams();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('query');

  const { collections, loading, hasMoreItems, nextPage } = useCollections(folderId, query);
  const { sort, edits, editMode, onEditModeChange, onEditChanges } = React.useContext(CollectionsContext);

  const empty = _.size(collections) == 0 && !loading;

  useEffect(() => {
    dispatch(invalidateCollections());
    dispatch(invalidateCollectionsSearchResults());
    fetchCollectionsForPage(0);
  }, [folderId, sort, query]);

  const fetchCollectionsForPage = (page: number) => {
    if (query) {
      const request = {
        query: query,
        list: folderId,
        page: page,
        limit: 20,
        highlight: false,
        sortBy: sort.sortBy,
        orderBy: sort.orderBy
      };

      dispatch(searchCollections(request));

    } else {
      const request = {
        list: folderId,
        page: page,
        limit: 20,
        ...sort
      };

      dispatch(fetchCollections(request));
    }
  }

  const handleLoadMoreCollections = () => {
    if (nextPage) {
      fetchCollectionsForPage(nextPage);
    }
  }

  const handleCollectionClick = (collection: Collection) => {
    navigate(`/collections/${collection.id}`);
  }

  const handleCreateCollection = () => {
    onEditModeChange('create');
  }

  if (empty) {
    return (
      <CollectionEmptyMessage list={folderId} searchQuery={query} onCreate={handleCreateCollection} />
    );
  }

  return (
    <InfiniteScroll className={styles.grid}
      loading={loading}
      hasMore={hasMoreItems}
      loadMore={handleLoadMoreCollections}
      loader={() => {
        return _.times(_.size(collections) == 0 ? 4 : 1).map((i) => (
          <PlaceholderCollectionCard key={i} />
        ));
      }}
    >
      {collections.map((collection, i) => {
        const edit = (edits || []).find((edit) => edit.id === _.get(collection, 'id'));
        const selected = _.get(edit, 'value') || false;

        return (
          <CollectionCard key={i}
            item={collection}
            editMode={editMode}
            selected={!!selected}
            onEditChanges={onEditChanges}
            onClick={handleCollectionClick}
          />
        )
      })}
    </InfiniteScroll>
  );
}

interface CollectionEmptyMessageProps {
  list: string | undefined;
  searchQuery: string | null;
  onCreate: () => void;
}

const CollectionEmptyMessage: React.FC<CollectionEmptyMessageProps> = ({ searchQuery, onCreate }) => {
  const message = useMemo(() => {
    if (searchQuery) {
      return "Well, This is Awkward. Your search didn't yield any results, try with a different search term";
    }

    return "Nothing to unpack here, yet, create or move collections to this blank space";
  }, [searchQuery]);

  const icon = useMemo(() => {
    if (searchQuery) {
      return '/images/collections/collections-search-empty.svg';
    }

    return '/images/collections/collections-empty.svg';
  }, [searchQuery]);

  return (
    <div className={styles.emptyContainer}>
      <EmptyMessage
        width={400}
        image={icon}
        heading={message}
        action={{
          title: "Create Collection",
          icon: 'paperflite-add',
          onClick: onCreate
        }}
      />
    </div>
  );
}

const CollectionMoveStatus: React.FC<object> = () => {
  const dispatch = useDispatch();

  const moveCompletions = useSelector((state) => {
    const moves: Record<string, any> = _.get(state, 'collections.move') || {};
    const items = _.keys(moves).map((key) => {
      return moves[key];
    }).filter((item) => {
      return item.moved;
    });

    return items;
  }, _.isEqual);

  useEffect(() => {
    if (_.size(moveCompletions) > 0) {
      moveCompletions.forEach((item) => {
        if (item.type === 'add') {
          const message = `Success! Collection ${item.collection.name} added to folder ${item.target.name}. Celebrate with a little dance!`;
          toast(<Toast.Sucess message={message} />, {
            toastId: item.id,
          });

          dispatch(invalidateCollectionMoveState(item.collection.id));
        } else if (item.type === 'remove') {
          const message = `Collection ${item.collection.name} removed from folder`;
          toast(<Toast.Sucess message={message} />, {
            toastId: item.id,
          });

          dispatch(invalidateCollectionMoveState(item.collection.id));
        }
      });
    }
  }, [moveCompletions]);

  return <></>;
}

export { CollectionsPage, CollectionsGrid };